// src/components/MainApp.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Message, Tab, Loader, Button } from 'semantic-ui-react';
import AzureForm from '../azure/components/form/azureForm';
import RequestForm3 from '../azure/components/form/RequestForm3';
import ApprovalView from './ApprovalView';
import Navbar from './Navbar';
import { useSecureAuth } from '../hooks/useSecureAuth';
import RequestHistory from './RequestHistory';
import ApprovalHistory from './ApprovalHistory';
import SuperAdminHistory from './SuperAdminHistory';
import AdminPanel from './AdminPanel';
import AllEventsView from './AllEventsView';
import SignInButton from './SignInButton';
import ReleaseToast from './ReleaseToast';
import AboutAndUserManual from './AboutAndUserManual';
import Settings from './Settings';
import WelcomeMessage from './WelcomeMessage';
import Footer from './Footer';
import LicenseGenerator from './LicenseGenerator';
import CloudCapacityDetails from './CloudCapacityDetails';
import axios from 'axios';
import { api } from '../api/apiClient';

const ACTIVE_DASHBOARD_TAB_STORAGE_KEY = 'tenantBridge.activeDashboardTab';
const LICENSE_GENERATOR_CLOUD_CACHE_KEY = 'tenantBridge.licenseGenerator.cloudDirectory.v1';

const MainApp = () => {
    const {
        user,
        detectedUser,
        authError,
        handleLogin,
        handleLogout,
        isAuthenticated,
        loading: authLoading,
        showWelcomeMessage,
        setShowWelcomeMessage
    } = useSecureAuth();

    const [userRoles, setUserRoles] = useState([]);
    const [userCategories, setUserCategories] = useState([]);
    const [userApproverCategories, setUserApproverCategories] = useState([]);
    const [userCategoriesSingleTenancy, setUserCategoriesSingleTenancy] = useState([]);
    const [userCategoriesMultiTenancy, setUserCategoriesMultiTenancy] = useState([]);
    const [userApproverCategoriesSingleTenancy, setUserApproverCategoriesSingleTenancy] = useState([]);
    const [userApproverCategoriesMultiTenancy, setUserApproverCategoriesMultiTenancy] = useState([]);
    const [linodeEnabled, setLinodeEnabled] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isAuthenticated) return;
        let isCancelled = false;
        const preloadCloudDirectory = async () => {
            try {
                const response = await api.clouds.search('a');
                if (isCancelled) return;
                const clouds = Array.isArray(response?.data?.clouds) ? response.data.clouds : [];
                const unique = new Map();
                clouds.forEach((cloud) => {
                    const cloudName = (cloud?.cloudName || '').toString().trim();
                    if (!cloudName || unique.has(cloudName)) return;
                    unique.set(cloudName, cloud);
                });
                const options = Array.from(unique.entries())
                    .map(([cloudName, cloud]) => {
                        const customerDisplay = (cloud?.customerName || '').toString().trim();
                        return {
                            key: cloudName,
                            value: cloudName,
                            text: customerDisplay && customerDisplay !== cloudName
                                ? `${cloudName} (${customerDisplay})`
                                : cloudName
                        };
                    })
                    .sort((a, b) => a.text.localeCompare(b.text));
                const meta = {};
                unique.forEach((cloud, name) => {
                    meta[name] = {
                        cloudName: (cloud?.cloudName || '').toString().trim() || name,
                        customerName: (cloud?.customerName || '').toString().trim() || null,
                        countryCode: (cloud?.countryCode || '').toString().trim() || null,
                        profile: (cloud?.profile || '').toString().trim() || null
                    };
                });
                window.localStorage.setItem(
                    LICENSE_GENERATOR_CLOUD_CACHE_KEY,
                    JSON.stringify({ options, meta })
                );
            } catch {
                // ignore preload failures; generator will lazy-load on search/open.
            }
        };
        preloadCloudDirectory();
        return () => {
            isCancelled = true;
        };
    }, [isAuthenticated]);

    // Dual-name window for subtitle and animation (until Nov 13, 2025)
    const showDualNameWindow = (() => {
        const now = new Date();
        const end = new Date('2025-11-13T23:59:59.999Z');
        return now <= end;
    })();

    // Login title swap animation state
    const [showOldTitle, setShowOldTitle] = useState(true);
    const [showTitleHighlight, setShowTitleHighlight] = useState(false);
    useEffect(() => {
        if (!isAuthenticated) {
            if (showDualNameWindow) {
                const id = setTimeout(() => setShowOldTitle(false), 2000);
                const highlightTimer = setTimeout(() => setShowTitleHighlight(true), 2050);
                const removeHighlight = setTimeout(() => setShowTitleHighlight(false), 3600);
                return () => {
                    clearTimeout(id);
                    clearTimeout(highlightTimer);
                    clearTimeout(removeHighlight);
                };
            } else {
                setShowOldTitle(false);
                setShowTitleHighlight(false);
            }
        } else {
            setShowOldTitle(true);
            setShowTitleHighlight(false);
        }
    }, [isAuthenticated, showDualNameWindow]);



    useEffect(() => {
        if (!isAuthenticated) {
            if (showDualNameWindow) {
                document.title = 'tenantBridge';
            } else {
                document.title = 'tenantBridge';
            }
        }
    }, [isAuthenticated, showDualNameWindow]);

    // Release notification state
    const [showReleaseToast, setShowReleaseToast] = useState(false);
    const [releaseToastContent, setReleaseToastContent] = useState({ title: '', message: '', icon: 'bell' });

    // Settings page state
    const [showSettingsPage, setShowSettingsPage] = useState(false);
    const [settingsUserManualSection, setSettingsUserManualSection] = useState(null);
    const [settingsInitialTab, setSettingsInitialTab] = useState(null);
    const [maintenanceModeEnabled, setMaintenanceModeEnabled] = useState(false);
    const [maintenanceModeLoading, setMaintenanceModeLoading] = useState(true);
    const [maintenanceAccessLoading, setMaintenanceAccessLoading] = useState(false);
    const [canAccessLoginDuringMaintenance, setCanAccessLoginDuringMaintenance] = useState(false);
    const [preReleaseAccessEnabled, setPreReleaseAccessEnabled] = useState(false);

    // Main content view mode and tab state
    const [viewMode, setViewMode] = useState('tabs'); // 'tabs' | 'events'
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const postLoginDefaultTabHandledRef = useRef(false);

    const saveActiveDashboardTab = useCallback((tabName) => {
        if (!tabName) return;
        try {
            localStorage.setItem(ACTIVE_DASHBOARD_TAB_STORAGE_KEY, String(tabName));
        } catch (error) {
            console.warn('Unable to persist active dashboard tab:', error);
        }
    }, []);

    const getSavedActiveDashboardTab = useCallback(() => {
        try {
            return localStorage.getItem(ACTIVE_DASHBOARD_TAB_STORAGE_KEY);
        } catch (error) {
            console.warn('Unable to read active dashboard tab:', error);
            return null;
        }
    }, []);

    // Role check utilities
    // Roles in DB/UI should be case-insensitive to avoid inconsistent gating.
    const hasRole = (role) => {
        const target = String(role).toUpperCase();
        return userRoles.map(r => String(r).toUpperCase()).includes(target);
    };
    const hasAnyRole = (roles) => {
        const normalizedUserRoles = new Set(userRoles.map(r => String(r).toUpperCase()));
        return roles.some(role => normalizedUserRoles.has(String(role).toUpperCase()));
    };

    useEffect(() => {
        const fetchMaintenanceMode = async () => {
            try {
                const response = await api.system.getMaintenanceMode();
                setMaintenanceModeEnabled(!!response.data?.enabled);
            } catch (maintenanceError) {
                console.error('Error fetching maintenance mode:', maintenanceError);
                setMaintenanceModeEnabled(false);
            } finally {
                setMaintenanceModeLoading(false);
            }
        };

        fetchMaintenanceMode();
        const intervalId = setInterval(fetchMaintenanceMode, 30000);
        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        const checkMaintenanceAccess = async () => {
            if (!maintenanceModeEnabled || isAuthenticated) {
                setCanAccessLoginDuringMaintenance(false);
                setMaintenanceAccessLoading(false);
                return;
            }

            const email = detectedUser?.email?.trim()?.toLowerCase();
            if (!email) {
                // If one-tap did not provide an email yet, do not hard-block login UI.
                // Backend /api/auth/google performs authoritative maintenance access checks.
                setCanAccessLoginDuringMaintenance(true);
                setMaintenanceAccessLoading(false);
                return;
            }

            setMaintenanceAccessLoading(true);
            try {
                const response = await api.system.getMaintenanceAccess(email);
                setCanAccessLoginDuringMaintenance(!!response.data?.canAccessLogin);
            } catch (error) {
                console.error('Error checking maintenance access:', error);
                setCanAccessLoginDuringMaintenance(false);
            } finally {
                setMaintenanceAccessLoading(false);
            }
        };

        checkMaintenanceAccess();
    }, [maintenanceModeEnabled, detectedUser, isAuthenticated]);

        // Check if user has admin panel access (based on DEVOPS role)
    const hasAdminPanelAccess = () => {
        return hasRole('DEVOPS');
    };

    const hasRequestForm3Access = () => {
        // Only show Multi Tenancy Form for MT_FORM_FILLER role
        return hasRole('MT_FORM_FILLER');
    };

    const handleForm3Submit = async (formData) => {
        try {
            const response = await axios.post('https://qa-tenantbridge.parablu.com/api/submit-request', {
                formData: {
                    ...formData,
                    submittedBy: user.email,
                    submittedAt: new Date().toISOString(),
                    requestForm3: true // Flag to identify this as Multi Tenancy Form
                }
            }, { withCredentials: true });

            return {
                success: true,
                message: response.data.message
            };
        } catch (error) {
            console.error('Multi Tenancy Form submit error:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'An unexpected error occurred'
            };
        }
    };

    // Fetch user roles function (can be called on demand)
    const fetchUserRoles = useCallback(async () => {
        if (isAuthenticated && user?.email) {
            try {
                setLoading(true);
                const response = await axios.get('https://qa-tenantbridge.parablu.com/api/user/roles', {
                    withCredentials: true
                });

                if (response.status === 200) {
                    setUserRoles(response.data.roles);
                    setUserCategories(response.data.categories || []);
                    setUserApproverCategories(response.data.approverCategories || []);
                    setUserCategoriesSingleTenancy(response.data.categoriesSingleTenancy || []);
                    setUserCategoriesMultiTenancy(response.data.categoriesMultiTenancy || []);
                    setUserApproverCategoriesSingleTenancy(response.data.approverCategoriesSingleTenancy || []);
                    setUserApproverCategoriesMultiTenancy(response.data.approverCategoriesMultiTenancy || []);
                    setLinodeEnabled(!!response.data.linodeEnabled);
                    setPreReleaseAccessEnabled(!!response.data.preReleaseAccessEnabled);
                    setError(null);

                    // After successful auth+roles load, fetch latest scheduled release toast.
                    try {
                        const releaseToastRes = await api.releaseToasts.getCurrent();
                        const latestReleaseToast = releaseToastRes.data?.notification;
                        if (latestReleaseToast?.title && latestReleaseToast?.description) {
                            setReleaseToastContent({
                                title: latestReleaseToast.title,
                                message: latestReleaseToast.description,
                                icon: latestReleaseToast.icon || 'bell'
                            });
                            setTimeout(() => setShowReleaseToast(true), 800);
                        } else {
                            setShowReleaseToast(false);
                        }
                    } catch (notificationError) {
                        console.error('Error fetching release toast:', notificationError);
                        setShowReleaseToast(false);
                    }
                }
            } catch (err) {
                console.error('Error fetching user roles:', err);
                setError('Failed to fetch user roles');
                setUserRoles([]);
                setUserCategories([]);
                setUserApproverCategories([]);
                setUserCategoriesSingleTenancy([]);
                setUserCategoriesMultiTenancy([]);
                setUserApproverCategoriesSingleTenancy([]);
                setUserApproverCategoriesMultiTenancy([]);
                setLinodeEnabled(false);
                setPreReleaseAccessEnabled(false);
            } finally {
                setLoading(false);
            }
        }
    }, [isAuthenticated, user]);

    // Fetch user roles effect
    useEffect(() => {
        fetchUserRoles();
    }, [fetchUserRoles]);

    // Restore the previously active dashboard tab after roles/tabs are available.
    useEffect(() => {
        if (!isAuthenticated || authLoading || loading) return;

        const tabs = getAvailableTabs();
        if (!tabs.length) return;

        // On fresh sign-in, prefer ST Form once, while keeping reload restore behavior unchanged.
        if (showWelcomeMessage && !postLoginDefaultTabHandledRef.current) {
            const stTabIndex = tabs.findIndex((tab) => String(tab.menuItem) === 'Single Tenancy Form');
            if (stTabIndex !== -1) {
                if (activeTabIndex !== stTabIndex) {
                    setActiveTabIndex(stTabIndex);
                }
                saveActiveDashboardTab('Single Tenancy Form');
                postLoginDefaultTabHandledRef.current = true;
                return;
            }
            postLoginDefaultTabHandledRef.current = true;
        }

        const savedTabName = getSavedActiveDashboardTab();
        if (!savedTabName) {
            if (activeTabIndex >= tabs.length) {
                setActiveTabIndex(0);
            }
            return;
        }

        const restoredIndex = tabs.findIndex((tab) => String(tab.menuItem) === savedTabName);
        if (restoredIndex !== -1) {
            if (activeTabIndex !== restoredIndex) {
                setActiveTabIndex(restoredIndex);
            }
            return;
        }

        if (activeTabIndex >= tabs.length) {
            setActiveTabIndex(0);
        }
        saveActiveDashboardTab(tabs[0].menuItem);
    }, [
        isAuthenticated,
        authLoading,
        loading,
        showWelcomeMessage,
        userRoles,
        userCategories,
        userApproverCategories,
        userCategoriesSingleTenancy,
        userCategoriesMultiTenancy,
        userApproverCategoriesSingleTenancy,
        userApproverCategoriesMultiTenancy,
        linodeEnabled,
        activeTabIndex,
        getSavedActiveDashboardTab,
        saveActiveDashboardTab
    ]);

    useEffect(() => {
        if (!isAuthenticated) {
            postLoginDefaultTabHandledRef.current = false;
        }
    }, [isAuthenticated]);

    // Listen for user update events to refresh roles immediately
    useEffect(() => {
        const handleUserUpdated = (event) => {
            const updatedEmail = event.detail?.email;
            // If the updated user is the current user, refresh their roles/categories
            if (updatedEmail && user?.email && updatedEmail.toLowerCase() === user.email.toLowerCase()) {
                console.log('User roles updated, refreshing...');
                fetchUserRoles();
            }
        };

        window.addEventListener('userUpdated', handleUserUpdated);
        return () => {
            window.removeEventListener('userUpdated', handleUserUpdated);
        };
    }, [user, fetchUserRoles]);


    // Release notification handlers
    const handleToastClose = useCallback(() => {
        setShowReleaseToast(false);
    }, []);

    const handleToastView = useCallback(() => {
        setShowReleaseToast(false);
    }, []);

    // // Click outside to close notification
    // const handleClickOutside = useCallback((e) => {
    //     if (showNotification && !e.target.closest('.release-notification')) {
    //         handleNotificationClose();
    //     }
    // }, [showNotification, handleNotificationClose]);

    // // Add click outside listener for notification
    // useEffect(() => {
    //     if (showNotification) {
    //         document.addEventListener('click', handleClickOutside);
    //         return () => {
    //             document.removeEventListener('click', handleClickOutside);
    //         };
    //     }
    // }, [showNotification, handleClickOutside]);

    // Form submission handler
    const handleSubmit = async (formData) => {
        try {
            const response = await axios.post('https://qa-tenantbridge.parablu.com/api/submit-request', {
                formData: {
                    ...formData,
                    submittedBy: user.email,
                    submittedAt: new Date().toISOString()
                }
            }, { withCredentials: true });

            return {
                success: true,
                message: response.data.message,
                requestId: response.data.requestId // Include requestId for auto-provision flow
            };
        } catch (error) {
            console.error('Submit error:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'An unexpected error occurred'
            };
        }
    };

    const getAvailableTabs = () => {
        const tabs = [];

        // Original Form Filler tab (keep existing logic unchanged)
        if (hasRole('ST_FORM_FILLER')) {
            tabs.push({
                menuItem: 'Single Tenancy Form',
                render: () => (
                    <Tab.Pane>
                        <AzureForm
                            onSubmit={handleSubmit}
                            userEmail={user.email}
                            roles={userRoles}
                            categories={userCategoriesSingleTenancy.length > 0 ? userCategoriesSingleTenancy : userCategories}
                            linodeEnabled={linodeEnabled}
                        />
                    </Tab.Pane>
                )
            });
        }

        // NEW: Multi Tenancy Form tab (only for MT_FORM_FILLER role)
        if (hasRequestForm3Access()) {
            tabs.push({
                menuItem: 'Multi Tenancy Form',
                render: () => (
                    <Tab.Pane>
                        <RequestForm3
                            onSubmit={handleForm3Submit}
                            userEmail={user.email}
                            roles={userRoles}
                            categories={userCategoriesMultiTenancy.length > 0 ? userCategoriesMultiTenancy : userCategories}
                        />
                    </Tab.Pane>
                )
            });
        }

        // My Requests tab - available for all roles including MT_FORM_FILLER (updated condition)
        if (hasAnyRole(['ST_FORM_FILLER', 'MT_FORM_FILLER', 'APPROVER', 'DEVOPS'])) {
            tabs.push({
                menuItem: 'My Submitted Requests',
                render: () => (
                    <Tab.Pane>
                        <RequestHistory
                            userEmail={user.email}
                            userRoles={userRoles}
                            userCategories={userCategories}
                            userCategoriesSingleTenancy={userCategoriesSingleTenancy}
                            userCategoriesMultiTenancy={userCategoriesMultiTenancy}
                            view="my-requests"
                        />
                    </Tab.Pane>
                )
            });
        }

        // License Generator tab - placed next to My Submitted Requests,
        // visible only for users with LICENSE_GENERATOR role
        if (hasRole('LICENSE_GENERATOR')) {
            tabs.push({
                menuItem: 'License Generator',
                render: () => (
                    <Tab.Pane>
                        <LicenseGenerator
                            userEmail={user?.email}
                            userName={user?.name}
                            userRoles={userRoles}
                        />
                    </Tab.Pane>
                )
            });
        }

        // Expiring Licenses tab - visible only for users with CAPACITY_READER role
        if (hasRole('CAPACITY_READER')) {
            tabs.push({
                menuItem: 'Expiring Licenses',
                render: () => (
                    <Tab.Pane>
                        <CloudCapacityDetails />
                    </Tab.Pane>
                )
            });
        }

        // Approver tab (keep existing logic unchanged)
        if (hasRole('APPROVER')) {
            tabs.push({
                menuItem: 'Approver View',
                render: () => (
                    <Tab.Pane>
                        <ApprovalView
                            userEmail={user.email}
                            userRoles={userRoles}
                            approverCategories={userApproverCategories}
                            approverCategoriesSingleTenancy={userApproverCategoriesSingleTenancy}
                            approverCategoriesMultiTenancy={userApproverCategoriesMultiTenancy}
                            viewType="approver"
                            linodeEnabled={linodeEnabled}
                        />
                    </Tab.Pane>
                )
            });
        }

        // Add Approval History tab for users with approver role (category-based history)
        if (hasAnyRole(['APPROVER'])) {
            tabs.push({
                menuItem: 'Approval History',
                render: () => (
                    <Tab.Pane>
                        <ApprovalHistory
                            userEmail={user.email}
                            userRoles={userRoles}
                            approverCategories={userApproverCategories}
                            approverCategoriesSingleTenancy={userApproverCategoriesSingleTenancy}
                            approverCategoriesMultiTenancy={userApproverCategoriesMultiTenancy}
                        />
                    </Tab.Pane>
                )
            });
        }

        // All Approval History: visible only to DevOps
        if (hasRole('DEVOPS')) {
            tabs.push({
                menuItem: 'All Approval History',
                render: () => (
                    <Tab.Pane>
                        <SuperAdminHistory userRoles={userRoles} />
                    </Tab.Pane>
                )
            });
        }

        // Add Admin Panel tab - restricted to DevOps only
        if (hasRole('DEVOPS')) {
            tabs.push({
                menuItem: 'Admin Panel',
                render: () => (
                    <Tab.Pane>
                        <AdminPanel />
                    </Tab.Pane>
                )
            });
        }

        return tabs;
    };

    const renderContent = () => {
        if (maintenanceModeLoading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <Loader active inline='centered' size='large'>
                        Loading
                    </Loader>
                </div>
            );
        }

        // Show Settings page if requested
        if (showSettingsPage) {
            return <Settings 
                user={user} 
                roles={userRoles} 
                onClose={() => {
                    setShowSettingsPage(false);
                    setSettingsUserManualSection(null);
                    setSettingsInitialTab(null);
                }}
                onLogout={handleLogout}
                userManualSection={settingsUserManualSection}
                initialTab={settingsInitialTab}
            />;
        }

        if (authLoading || loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <Loader active inline='centered' size='large'>
                        Loading
                    </Loader>
                </div>
            );
        }

        if (!isAuthenticated) {
            if (maintenanceModeEnabled) {
                if (maintenanceAccessLoading) {
                    return (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                            <Loader active inline='centered' size='large'>
                                Loading
                            </Loader>
                        </div>
                    );
                }

                if (!canAccessLoginDuringMaintenance) {
                    return (
                        <div
                            className="login-container"
                            style={{
                                background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
                                minHeight: 'calc(100vh - 120px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '2rem 1rem'
                            }}
                        >
                            <div
                                style={{
                                    width: '100%',
                                    maxWidth: '700px',
                                    margin: '0 auto',
                                    background: '#ffffff',
                                    borderRadius: '18px',
                                    boxShadow: '0 20px 45px rgba(15, 23, 42, 0.10)',
                                    border: '1px solid #e2e8f0',
                                    padding: '2.4rem 2.4rem 2.1rem',
                                    textAlign: 'center'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.1rem' }}>
                                    <img src="/logo512.png" alt="tenantBridge" style={{ height: '56px', width: '56px' }} />
                                </div>
                                <h2 style={{ margin: 0, fontSize: '2rem', lineHeight: 1.2, fontWeight: 700, color: '#0f172a' }}>
                                    TenantBridge — Maintenance in Progress
                                </h2>
                                <p style={{ margin: '1rem auto 0', maxWidth: '560px', color: '#334155', lineHeight: 1.7, fontSize: '1.05rem' }}>
                                    TenantBridge is temporarily unavailable while we perform scheduled maintenance and deployment activities.
                                </p>
                                <p style={{ marginTop: '0.75rem', marginBottom: 0, color: '#334155', lineHeight: 1.7, fontSize: '1.05rem' }}>
                                    Service will be restored shortly.
                                </p>
                                <p style={{ marginTop: '0.75rem', marginBottom: 0, color: '#475569', lineHeight: 1.7, fontSize: '1.02rem' }}>
                                    We apologize for any inconvenience and appreciate your understanding.
                                </p>
                            </div>
                        </div>
                    );
                }
            }

            return (
                <div className="login-container">
                    <div className="login-panel">
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                            <img src="/logo512.png" alt="tenantBridge" style={{ height: '56px', width: '56px' }} />
                        </div>
                        <Message info style={{ maxWidth: '100%', textAlign: 'center' }}>
                            <div className="tb-title-swap">
                                <div className={`tb-title-line ${showOldTitle ? 'tb-title-visible' : 'tb-title-hidden'}`}>
                                    <Message.Header>Welcome to Azure/Linode VM Portal</Message.Header>
                                </div>
                                <div className={`tb-title-line ${showOldTitle ? 'tb-title-hidden' : 'tb-title-visible'}`}>
                                    <div className={showTitleHighlight ? 'tb-title-emphasis' : ''} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                        <Message.Header style={{ marginBottom: 0 }}>Welcome to tenantBridge</Message.Header>
                                    </div>
                                    {showTitleHighlight && (
                                        <div className="tb-title-underline" />
                                    )}
                                </div>
                            </div>
                            {showDualNameWindow && !showOldTitle && (
                                <div style={{ marginTop: '6px', color: 'rgba(0,0,0,0.6)', fontSize: '0.95rem' }}>
                                    formerly Azure/Linode VM Portal
                                </div>
                            )}
                            <p style={{ marginTop: '12px' }}>Please sign in with your CrashPlan Google account to continue.</p>
                            {authError && (
                                <Message negative>
                                    <Message.Header>
                                        {authError.includes('tenantBridge Team') ? 'User Not Found' : 'Authentication Error'}
                                    </Message.Header>
                                    <p>{authError}</p>
                                    {authError.includes('@crashplan.com') && (
                                        <p>Please sign in with your CrashPlan Google account.</p>
                                    )}
                                </Message>
                            )}
                        </Message>

                        <div className="login-actions">
                            <SignInButton
                                onClick={handleLogin}
                                loading={authLoading}
                                user={detectedUser || user}
                            />
                        </div>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <Message negative>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            );
        }

        if (!userRoles.length) {
            return (
                <Message warning>
                    <Message.Header>No roles assigned</Message.Header>
                    <p>You don't have any roles assigned in the system. Please contact an administrator.</p>
                </Message>
            );
        }

        if (maintenanceModeEnabled && !hasRole('DEVOPS') && !preReleaseAccessEnabled) {
            return (
                <div className="login-container">
                    <div className="login-panel">
                        <Message warning style={{ maxWidth: '100%', textAlign: 'center' }}>
                            <Message.Header>Maintenance mode is enabled</Message.Header>
                            <p style={{ marginTop: '0.75rem' }}>
                                Access is currently restricted to DEVOPS users.
                            </p>
                        </Message>
                        <div className="login-actions">
                            <Button onClick={handleLogout}>
                                Sign Out
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        // Dedicated All Events view (moved from main tabs into header profile)
        if (viewMode === 'events' && hasRole('DEVOPS')) {
            return (
                <div>
                    <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-start' }}>
                        <Button
                            icon="arrow left"
                            content="Back to Dashboard"
                            basic
                            onClick={() => setViewMode('tabs')}
                        />
                    </div>
                    <AllEventsView />
                </div>
            );
        }

        const tabs = getAvailableTabs();

        return (
            <Tab 
                panes={tabs} 
                activeIndex={activeTabIndex}
                onTabChange={(e, { activeIndex }) => {
                    setActiveTabIndex(activeIndex);
                    const tabName = tabs[activeIndex]?.menuItem;
                    saveActiveDashboardTab(tabName);
                }}
            />
        );
    };

    // Listen for event to switch to All Events view (triggered from notifications/header)
    useEffect(() => {
        const handleSwitchToAllEvents = () => {
            // Ensure we are on the main application view (not Settings)
            setShowSettingsPage(false);
            setSettingsUserManualSection(null);
            setSettingsInitialTab(null);

            // Use a small delay to ensure state updates settle
            setTimeout(() => {
                if (hasRole('DEVOPS')) {
                    setViewMode('events');
                }
            }, 100);
        };

        window.addEventListener('switchToAllEventsTab', handleSwitchToAllEvents);
        return () => {
            window.removeEventListener('switchToAllEventsTab', handleSwitchToAllEvents);
        };
    }, [userRoles, user, hasRole]);

    return (
        <div className="app" style={{ maxWidth: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Navbar
                user={user}
                roles={userRoles}
                onLogout={handleLogout}
                onLogin={handleLogin}
                loading={authLoading}
                onSettingsClick={isAuthenticated ? () => setShowSettingsPage(true) : undefined}
                onProfileClick={isAuthenticated ? () => {
                    setSettingsInitialTab(2);
                    setShowSettingsPage(true);
                } : undefined}
                onViewAllEvents={isAuthenticated && hasRole('DEVOPS') ? () => {
                    setShowSettingsPage(false);
                    setSettingsUserManualSection(null);
                    setSettingsInitialTab(null);
                    setViewMode('events');
                } : undefined}
            />
            <Container className="main-container page-fade-in" fluid style={{ flex: '1' }}>
                {renderContent()}
            </Container>

            {/* Footer */}
            <Footer />

            {/* Release Toast */}
            <ReleaseToast
                open={showReleaseToast}
                onClose={handleToastClose}
                onView={handleToastView}
                autoCloseDelay={5000}
                title={releaseToastContent.title}
                message={releaseToastContent.message}
                icon={releaseToastContent.icon}
                showViewButton={false}
            />

            {/* Welcome Message */}
            {showWelcomeMessage && user && (
                <WelcomeMessage 
                    user={user}
                    onClose={() => setShowWelcomeMessage(false)}
                />
            )}
        </div>
    );
};

export default MainApp;